import { ReactPlugin, withAITracking } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import { ConfigurationService } from '../services/ConfigurationService';
import { createBrowserHistory } from "history";
import StartupStore from "./StartupStore";
export interface IAnalyticsStore {
  reactPlugin: ReactPlugin;
  applicationInsights: ApplicationInsights;
  isInitialized: boolean;
  initApplicationInsights: () => void;
  sendPageView: (name: string) => void;
  sendExceptionEvent: (action: string) => void;
  sendAddUserEvent: (action: string) => void;
  sendRevokeUserEvent: (action: string) => void;
  sendGrantUserEvent: (action: string) => void;
  sendRegenerateCSVEvent: (action: string) => void;
  sendSessionJsonEvent: (action: string) => void;
  sendUpdateLincensesEvent: (action: string) => void;
  sendManageFeaturesEvent: (action: string) => void;
  sendInviteEvent: (action: string) => void;
  sendResendInviteEvent: (action: string) => void;
  sendCancelInviteEvent: (action: string) => void;
  sendReplacePlayerInSessionEvent: (action: string) => void;
}
export class AnalyticsStore implements IAnalyticsStore {
  private _startupStore: StartupStore;
  private _configService: ConfigurationService;
  isInitialized: boolean = false;
  constructor(startupStore: StartupStore, configService: ConfigurationService) {
    makeAutoObservable(this);
    this._startupStore = startupStore;
    this._configService = configService;
  }
  reactPlugin: ReactPlugin;
  applicationInsights: ApplicationInsights;
  initApplicationInsights = async () => {
    if(!this.isInitialized){
      const configs = await this._configService.getConfigs();
      const appInsightsInstrumentationKey = configs?.applicationInsightsInstrumentationKey;
      const browserHistory = createBrowserHistory();
      this.reactPlugin = new ReactPlugin();
      this.applicationInsights = new ApplicationInsights({
        config: {
          instrumentationKey: appInsightsInstrumentationKey,
          endpointUrl: 'applicationInsightsProxy/proxyTelemetryData',
          enableAutoRouteTracking: false,
          extensions: [this.reactPlugin],
          extensionConfig: {
            [this.reactPlugin.identifier]: { history: browserHistory }
          }
        }
      });
      this.applicationInsights.loadAppInsights();
      runInAction(() => { this.isInitialized = true; });
    }
  };
  sendPageView = (name: string) => {
    if (!this.isInitialized) {
      setTimeout(() => {
        this.sendPageView(name);
      }, 200);
    } else {
      let path = window.location.pathname + window.location.search;
      let props = { 
        userId: this._startupStore.bearerTokenProfileInfo?.userId,
        organizationId: this._startupStore.bearerTokenProfileInfo?.organizationId
      }
      this.applicationInsights.trackPageView({ name: name, uri: path, properties: props })
      this.applicationInsights.trackPageViewPerformance({ name: name, uri: path, properties: props})
    }
  };
  public sendExceptionEvent = (action: string) => {
    this.sendEvent('Exception', action);
  };
  public sendAddUserEvent = (action: string) => {
    this.sendEvent('Users', action);
  };
  public sendGrantUserEvent = (action: string) => {
    this.sendEvent('Users', action)
  }
  public sendRevokeUserEvent = (action: string) => {
    this.sendEvent('Users', action)
  }
  public sendRegenerateCSVEvent = (action: string) => {
    this.sendEvent('Sessions', action);
  }
  public sendSessionJsonEvent = (action: string) => {
    this.sendEvent('Sessions', action);
  }
  public sendDiagnosticMessageEvent = (action: string) => {
    this.sendEvent('DiagnosticMessages', action);
  }
  public sendUpdateLincensesEvent = (action: string) => {
    this.sendEvent('Organization', action);
  }
  public sendManageFeaturesEvent = (action: string) => {
    this.sendEvent('Organization', action);
  }
  public sendInviteEvent = (action: string) => {
    this.sendEvent('Invites', action);
  }
  public sendResendInviteEvent = (action: string) => {
    this.sendEvent('Invites', action);
  }
  public sendCancelInviteEvent = (action: string) => {
    this.sendEvent('Invites', action);
  }
  public sendLoggedOutModalEvent = (action: string) => {
    this.sendEvent('LoggedOutModal', action);
  }
  public sendReplacePlayerInSessionEvent = (action: string) => {
    this.sendEvent('Sessions', action);
  }
  private sendEvent = (category: string, action: string, value?: number) => {
    const event = {
      category: category,
      action: action,
      userId: this._startupStore.bearerTokenProfileInfo?.userId,
      organizationId: this._startupStore.bearerTokenProfileInfo?.organizationId,
      value: value ? value : undefined,
    };
   
    if (!this.isInitialized) {
      setTimeout(() => {
        this.sendEvent(category, action);
      }, 200);
    } else {
      this.applicationInsights.trackEvent({
        name: event.category,
        properties:
        {
          action: event.action,
          userId: event.userId,
          organizationId: event.organizationId,
          value: event.value
        }
      });
    }
  };
}
export default AnalyticsStore;